import { FC, useEffect, useRef, useState } from "react";
import { checkmarkCircle } from "ionicons/icons";
import { Trans, useTranslation } from "react-i18next";
import styled, { keyframes } from "styled-components";
import Container from "../../../atoms/Container";
import VeriticalCenter from "../../../atoms/VerticalCenter";
import NextPrevButtons from "../../../molecules/NextPrevButtons";
import { useResonanceLayout } from "../../ResonanceContext/ResonanceLayoutContext";
import { useIonRouter } from "@ionic/react";
import PWAPrompt from "react-ios-pwa-prompt";
import { MINI_LOGO } from "../../../../constants/images";
import { isStandalone, useAndroidPwaPrompt } from "../../../../hooks/useAndroidPwaPrompt";
import useToast from "../../../../hooks/useToast";
import { useStudySlug } from "../../../../hooks/useStudySlug";
import { getDefaultTabPath } from "../../../../navigation/studyTabs";
import { useConfig } from "../../../../contexts/ConfigContext";
import { getProjectConfig } from "../../../../projects";
import { WELCOME_BACKGROUND } from "../../../../theme/welcomeBackground";

type DeviceType = "iOS" | "Android" | "Desktop";

const DisplayRegistredSuccessfully: FC = () => {
  const { t } = useTranslation();
  const study_slug = useStudySlug();
  const project = getProjectConfig(study_slug);
  // selectedRole here is the SIGNUP role (set by the flow) — correct for the
  // role-aware landing redirect below.
  const { app, selectedRole } = useConfig();
  const ionRouter = useIonRouter();
  const { setResonanceLayout } = useResonanceLayout();

  const [deviceType, setDeviceType] = useState<DeviceType>("Desktop");
  const [iosPromptVisible, setIosPromptVisible] = useState(false);

  const { isReady: androidReady, promptInstall, installed } = useAndroidPwaPrompt();
  // Card-language success toast (.success-toast in theme/common.scss): centered
  // mid-page with the teal check — the default dark bottom pill sat on the tab
  // bar and read closer to an error than a celebration.
  const presentSuccess = useToast({
    position: "middle",
    cssClass: "success-toast",
    icon: checkmarkCircle,
    duration: 2500,
  });
  const skippedRef = useRef(false);

  useEffect(() => {
    setResonanceLayout({
      disableHeader: true,
      disableNav: true,
      // Same uniform themed tint as the landing — this is the very next screen in
      // the signup flow, so the page ground carries through instead of the old
      // near-white surface color the card was invisible against.
      backgroundColor: WELCOME_BACKGROUND,
      welcomePage: false,
    });
    return () =>
      setResonanceLayout({
        disableHeader: false,
        disableNav: false,
        backgroundColor: "white",
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const ua = navigator.userAgent || navigator.vendor;
    if (/android/i.test(ua)) setDeviceType("Android");
    else if (/iPad|iPhone|iPod/.test(ua)) setDeviceType("iOS");
    else setDeviceType("Desktop");
  }, []);

  // For iOS, we only show the modal (no card)
  useEffect(() => {
    if (deviceType === "iOS") {
      // small delay so the page content settles before the modal
      const id = setTimeout(() => setIosPromptVisible(true), 500);
      return () => clearTimeout(id);
    } else {
      setIosPromptVisible(false);
    }
  }, [deviceType]);

  const handleNext = () => {
    // Soft, in-app navigation into the tabs. This works because by now the signup
    // flow has finished on this page (the landing route stopped auto-redirecting
    // mid-flow via signupInProgress), so this is the single clean entry into the
    // tab outlet — the same model that worked before single-study mode.
    ionRouter.push(getDefaultTabPath(study_slug, selectedRole?.name), "root", "replace", { unmount: true });
  };

  // Desktop browser with nothing to offer — no captured install prompt (Firefox/
  // Safari, or Chrome/Edge that deems the app uninstallable) or already running
  // installed — would render only "congrats + Suivant". Skip the page: toast the
  // congratulations (app-level Ionic toast, 2s — survives the navigation) and
  // enter the app directly. The UA and the global prompt capture are read
  // SYNCHRONOUSLY here: the deviceType/isReady STATES are set by earlier mount
  // effects and wouldn't be visible to this effect in the same commit. The ref
  // guards StrictMode's double effect run (one toast, one navigation).
  useEffect(() => {
    const ua = navigator.userAgent || navigator.vendor;
    const isMobile = /android|iPad|iPhone|iPod/i.test(ua);
    const promptCaptured = !!window.__deferredA2HS;
    if (!isMobile && (!promptCaptured || isStandalone()) && !skippedRef.current) {
      skippedRef.current = true;
      presentSuccess(t("home_registred_successfully"));
      handleNext();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Android keeps its full card (install button when ready, manual hint otherwise).
  // Desktop shows the card ONLY when beforeinstallprompt was actually captured —
  // i.e. Chrome/Edge with an installable manifest — so the manual hint (which would
  // describe a feature Firefox/Safari desktop don't have) can never render there.
  // The hook is platform-agnostic despite its name (pure beforeinstallprompt).
  const showInstallCard =
    !installed &&
    (deviceType === "Android" || (deviceType === "Desktop" && androidReady));

  return (
    <Background>
      <Container>
        <VeriticalCenter style={{ alignItems: "center", marginTop: "6vh" }}>
          {/* Caps the content on wide screens so the Suivant pill (flex-end inside
              NextPrevButtons) anchors to the card column's right edge instead of
              drifting to the far edge of the 1200px page. Full-bleed on phones so
              the pill keeps its edge-hugging shape. */}
          <Column>
          <Icon alt="registered successfully" />
          <Title>{t("home_registred_successfully")}</Title>

          {/* ANDROID + installable DESKTOP (Chrome/Edge): the install card & CTA */}
          {showInstallCard && (
            <HeroCard role="status" aria-live="polite">
              <Logo src={project.miniLogo ?? project.logo ?? MINI_LOGO} alt={app?.name} />
              <Headline>
                {t("add_to_home_screen_prompt")}
              </Headline>

              <InstallRow>
                {androidReady ? (
                  <PrimaryButton onClick={promptInstall} aria-label={t("pwa_install_button_aria")}>
                    {t("pwa_install_button")}
                  </PrimaryButton>
                ) : (
                  <Hint>
                    <Trans
                      i18nKey="pwa_install_hint"
                      components={{ kbd: <kbd />, strong: <strong /> }}
                    />
                  </Hint>
                )}
              </InstallRow>
            </HeroCard>
          )}

          {/* iOS: ONLY the PWAPrompt modal — no card */}
          {deviceType === "iOS" && (
            <PWAPrompt
              isShown={iosPromptVisible}
              onClose={() => setIosPromptVisible(false)}
              appIconPath={project.miniLogo ?? project.logo ?? "/assets/images/logo.png"}
              copyTitle={t("pwa_ios_title")}
              copySubtitle={t("pwa_ios_subtitle")}
              copyDescription={t("pwa_ios_description")}
              copyShareStep={t("pwa_ios_share_step")}
              copyAddToHomeScreenStep={t("pwa_ios_add_step")}
              delay={0}           // we control visibility via isShown
              timesToShow={1}     // show once
              promptOnVisit={1}   // and only on this visit
            />
          )}

          <NextPrevButtons onNextClick={handleNext} />
          </Column>
        </VeriticalCenter>
      </Container>
    </Background>
  );
};

/* ---------- Styles ---------- */

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(12px) scale(.98); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

const Background = styled.div`
  min-height: 100vh;
`;

/* Grows to fill the page column so NextPrevButtons' margin-top:auto keeps working;
   capped on wide screens so content and the Suivant pill stay one composed block.
   Full width on phones — the pill's flush-edge shape depends on it. */
const Column = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1 0 auto;
  width: 100%;

  @media (min-width: 800px) {
    max-width: 720px;
    margin: 0 auto;
  }
`;

/* Same card language as the landing's login Panel (background token on the themed
   page tint, hairline text-ink border, emphasis top bar) — the two signup screens
   read as one flow. The old translucent white glass was invisible on the
   near-white page. */
const HeroCard = styled.section`
  position: relative;
  width: min(720px, 90vw);
  margin: 20px auto 28px;
  padding: clamp(18px, 3vw, 28px);
  border-radius: 20px;
  overflow: hidden;
  animation: ${fadeUp} 0.36s ease-out both;

  background: var(--app-color-background, #ffffff);
  border: 1px solid rgba(var(--app-color-text-rgb, 4, 23, 35), 0.08);
  box-shadow: 0 12px 34px rgba(var(--app-color-text-rgb, 4, 23, 35), 0.08);
  text-align: center;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(
      90deg,
      var(--app-color-emphasis, var(--app-color-primary, #2db6b4)),
      var(--app-color-emphasis-soft, var(--app-color-primary-tint, #abf0ef))
    );
  }
`;

const Icon = styled.img`
  width: 96px;
  content: url(/assets/images/svg/verified-green.svg);
  margin: 0 0 6px;
`;

const Title = styled.h1`
  font-size: clamp(20px, 2.2vw, 28px);
  margin: 0 0 50px;
  font-weight: 800;
  /* Heading ink on the white glass card — text token, NOT primary.contrast
     (contrast is the on-primary color and can be white on light cards). */
  color: var(--app-color-text, #0d1b2a);
  text-align: center;
`;

const Logo = styled.img`
  width: 88px;
  height: 88px;
  border-radius: 20px;
  margin: 6px auto 14px;
  box-shadow: 0 6px 14px rgba(0, 0, 0, 0.08);
`;

const Headline = styled.p`
  font-size: clamp(16px, 1.8vw, 20px);
  margin: 0 auto 12px;
  line-height: 1.4;
  color: var(--app-color-text, #1f2a44);
`;

const InstallRow = styled.div`
  display: grid;
  gap: 14px;
  align-items: center;
  justify-items: center;
  margin-top: 10px;
`;

const kbdStyle = `
  font-family: inherit;
  font-weight: 700;
  padding: 0 8px;
  border-radius: 6px;
  background: rgba(0,0,0,.06);
  border: 1px solid rgba(0,0,0,.08);
`;

const Hint = styled.p`
  margin: 0;
  color: rgba(var(--app-color-text-rgb, 4, 23, 35), 0.75);
  font-size: 15px;
  kbd { ${kbdStyle} }
  strong { font-weight: 800; }
`;

const PrimaryButton = styled.button`
  appearance: none;
  border: 0;
  padding: 12px 18px;
  border-radius: 999px;
  font-size: 16px;
  font-weight: 800;
  letter-spacing: 0.2px;
  cursor: pointer;

  /* CTA -> secondary family (see tokens.ts roles). */
  background: linear-gradient(180deg, var(--app-color-secondary, #2563eb) 0%,
    var(--app-color-secondary, #2563eb) 85%) no-repeat;
  color: var(--app-color-secondary-contrast, #fff);
  box-shadow:
    0 10px 20px rgba(var(--app-color-secondary-rgb, 37, 99, 235), 0.24),
    0 2px 6px rgba(var(--app-color-secondary-rgb, 37, 99, 235), 0.2);
  transition: transform 0.08s ease, box-shadow 0.12s ease, filter 0.12s ease;

  &:hover { transform: translateY(-1px); filter: brightness(1.02); }
  &:active { transform: translateY(0); box-shadow:
    0 6px 14px rgba(var(--app-color-secondary-rgb, 37, 99, 235), 0.22),
    0 1px 4px rgba(var(--app-color-secondary-rgb, 37, 99, 235), 0.2); }
`;

export default DisplayRegistredSuccessfully;
